import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import SortableList, { DragHandle } from '../components/admin/SortableList';
import {
  LayoutDashboard, Users, BookOpen, Video, HelpCircle,
  Award, Settings, Tag, TrendingUp, Menu, X, Sun, Moon,
  Plus, Edit, Trash2, Eye, EyeOff, Crown, DollarSign, UserCheck,
  ChevronRight, LogOut, Search, MoreHorizontal, Database,
  BarChart3, Image, Share2, Radio, Languages, Mail, Download, Upload,
  FileText, Key, UserPlus, Shield, Megaphone, Unlock, HardDrive, Receipt,
  Wrench, AlertTriangle, BarChart2, Phone, Globe, CreditCard, RefreshCw, Copy,
  Activity
} from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import PromoPopup from '../components/PromoPopup';
import QRCodeDisplay from '../components/QRCodeDisplay';
import { categoryLabel, categoryId, safeArray, safeNum } from '../lib/utils';
import { googleFontsHref, DEFAULT_HEADING_FONT, DEFAULT_BODY_FONT } from '../lib/fonts';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

// Admin Layout
export const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isSuperAdmin, hasPermission, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [maintenanceActive, setMaintenanceActive] = useState(false);

  useEffect(() => {
    if (!token) navigate('/login');
    else if (!isAdmin) navigate('/');
  }, [token, isAdmin, navigate]);

  // Reminder banner — public /settings so it works for any admin role, not just super_admin
  useEffect(() => {
    axios.get(`${API_URL}/settings`)
      .then((res) => setMaintenanceActive(!!res.data?.maintenance_mode))
      .catch(() => {});
  }, [location.pathname]);

  // Grouped into sections for the sidebar (see PROGRESS.md's Pricing/GST/Payments
  // reorg entry) — `section` drives the header rendered above each group below.
  const allMenuItems = useMemo(
    () => [
      { icon: LayoutDashboard, labelKey: 'admin_nav_dashboard', label: 'Dashboard', path: '/admin', section: 'Platform' },

      { icon: BookOpen, labelKey: 'admin_nav_courses', label: 'Courses', path: '/admin/courses', permission: 'manage_courses', section: 'Content' },
      { icon: Tag, labelKey: 'admin_nav_categories', label: 'Categories', path: '/admin/categories', permission: 'manage_courses', section: 'Content' },
      { icon: Video, labelKey: 'admin_nav_lessons', label: 'Lessons', path: '/admin/lessons', permission: 'manage_lessons', section: 'Content' },
      { icon: HelpCircle, labelKey: 'admin_nav_quizzes', label: 'Quizzes', path: '/admin/quizzes', permission: 'manage_quizzes', section: 'Content' },
      { icon: Radio, labelKey: 'admin_nav_live', label: 'Live Classes', path: '/admin/live-classes', permission: 'manage_live_classes', section: 'Content' },
      { icon: Image, labelKey: 'admin_nav_projects', label: 'Projects', path: '/admin/projects', permission: 'manage_projects', section: 'Content' },
      { icon: Award, labelKey: 'admin_nav_certificates', label: 'Certificates', path: '/admin/certificates', permission: 'manage_certificates', section: 'Content' },
      { icon: BarChart3, labelKey: 'admin_nav_analytics', label: 'Video Analytics', path: '/admin/analytics', permission: 'view_analytics', section: 'Content' },

      { icon: Users, labelKey: 'admin_nav_users', label: 'Users', path: '/admin/users', permission: 'manage_users', section: 'Users' },
      { icon: Phone, labelKey: 'admin_nav_crm', label: 'CRM & Leads', path: '/admin/crm', permission: 'manage_crm', section: 'Users' },
      { icon: Shield, labelKey: 'admin_nav_team', label: 'Team & Roles', path: '/admin/team', superAdminOnly: true, section: 'Users' },

      { icon: DollarSign, labelKey: 'admin_nav_coupons', label: 'Coupons', path: '/admin/coupons', permission: 'manage_coupons', section: 'Revenue' },
      { icon: TrendingUp, labelKey: 'admin_nav_revenue', label: 'Revenue', path: '/admin/revenue', permission: 'view_revenue', section: 'Revenue' },
      { icon: FileText, labelKey: 'admin_nav_orders', label: 'Orders & Invoices', path: '/admin/orders', permission: 'view_revenue', section: 'Revenue' },
      { icon: Share2, labelKey: 'admin_nav_affiliates', label: 'Affiliates', path: '/admin/affiliates', permission: 'manage_affiliates', section: 'Revenue' },

      { icon: Globe, labelKey: 'admin_nav_country_pricing', label: 'Country Pricing', path: '/admin/country-pricing', permission: 'manage_settings', section: 'Pricing & Payments' },
      { icon: CreditCard, labelKey: 'admin_nav_payment_gateways', label: 'Payment Gateways', path: '/admin/payment-gateways', permission: 'manage_integrations', section: 'Pricing & Payments' },
      { icon: Receipt, labelKey: 'admin_nav_billing', label: 'Billing & Tax', path: '/admin/billing', permission: 'manage_settings', section: 'Pricing & Payments' },
      { icon: Receipt, labelKey: 'admin_nav_gst_settings', label: 'GST & Billing (India)', path: '/admin/gst-settings', permission: 'manage_settings', section: 'Pricing & Payments' },

      { icon: Megaphone, labelKey: 'admin_nav_share_rewards', label: 'Share Rewards', path: '/admin/share-rewards', permission: 'manage_marketing', section: 'Marketing' },
      { icon: Mail, labelKey: 'admin_nav_email', label: 'Email', path: '/admin/email', permission: 'send_notifications', section: 'Marketing' },
      { icon: BarChart2, labelKey: 'admin_nav_analytics_settings', label: 'Analytics & Tracking', path: '/admin/analytics-settings', permission: 'manage_settings', section: 'Marketing' },

      { icon: Settings, labelKey: 'admin_nav_settings', label: 'Settings', path: '/admin/settings', permission: 'manage_settings', section: 'System' },
      { icon: FileText, labelKey: 'admin_nav_legal', label: 'Legal Pages', path: '/admin/legal', permission: 'manage_settings', section: 'System' },
      { icon: Key, labelKey: 'admin_nav_integrations', label: 'Integrations', path: '/admin/integrations', permission: 'manage_integrations', section: 'System' },
      { icon: HardDrive, labelKey: 'admin_nav_backup', label: 'Backup & Restore', path: '/admin/backup', superAdminOnly: true, section: 'System' },
      { icon: Activity, labelKey: 'admin_nav_server_monitor', label: 'Server Monitor', path: '/admin/server', permission: 'manage_settings', section: 'System' },
    ],
    []
  );

  const menuItems = useMemo(
    () => allMenuItems.filter((item) => {
      if (item.superAdminOnly) return isSuperAdmin;
      if (item.permission) return hasPermission(item.permission);
      return true;
    }),
    [allMenuItems, isSuperAdmin, hasPermission]
  );

  // Group the filtered items by section, preserving section order-of-first-appearance.
  const menuSections = useMemo(() => {
    const order = [];
    const bySection = {};
    menuItems.forEach((item) => {
      const key = item.section || '';
      if (!bySection[key]) { bySection[key] = []; order.push(key); }
      bySection[key].push(item);
    });
    return order.map((key) => ({ section: key, items: bySection[key] }));
  }, [menuItems]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!isAdmin) return null;

  return (
    <div
      className="min-h-screen bg-background flex"
      style={{ '--font-heading': "'Playfair Display', serif", '--font-body': "'Manrope', sans-serif" }}
    >
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-card border-r transition-all duration-300 flex flex-col`}>
        <div className="p-4 flex items-center justify-between border-b">
          {sidebarOpen && (
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <span className="text-accent-foreground font-serif font-bold">T</span>
              </div>
              <span className="font-serif text-lg font-medium">THACHARI</span>
            </Link>
          )}
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {maintenanceActive && (
          <Link
            to="/admin/settings"
            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition-colors"
            data-testid="maintenance-active-banner"
          >
            <span className="shrink-0">🔴</span>
            {sidebarOpen && <span>{t('admin_maintenance_banner', 'Maintenance Mode Active')}</span>}
          </Link>
        )}

        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {menuSections.map((group) => (
              <div key={group.section || 'ungrouped'} className="mb-3 last:mb-0">
                {sidebarOpen && group.section && (
                  <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {group.section}
                  </p>
                )}
                {group.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  const navId = item.path.replace(/\//g, '-').replace(/^-/, '') || 'admin';
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      data-testid={`admin-nav-${navId}`}
                    >
                      <item.icon className="w-5 h-5 shrink-0" />
                      {sidebarOpen && (
                        <span className="text-sm font-medium">
                          {t(item.labelKey, item.label)}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t space-y-2">
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
            {sidebarOpen &&
              (theme === 'dark'
                ? t('admin_light_mode', 'Light Mode')
                : t('admin_dark_mode', 'Dark Mode'))}
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start text-destructive" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            {sidebarOpen && t('admin_logout', 'Logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

// Admin Dashboard
export const AdminDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/analytics`, { headers });
        setAnalytics(res?.data || null);
      } catch (err) {
        console.error(err);
        setAnalytics(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [token]);

  const seedData = async () => {
    try {
      const res = await axios.post(`${API_URL}/admin/seed-data`, {}, { headers });
      const msg = res?.data?.message || 'Done';
      toast.success(res?.data?.skipped ? msg : `${msg} — reloading`);
      window.location.reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const text =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || d).join('; ')
            : err?.message;
      toast.error(text || t('admin_seed_toast_fail', 'Failed to seed data'));
    }
  };

  if (loading) {
    return <div className="animate-pulse">{t('admin_loading_analytics', 'Loading analytics...')}</div>;
  }

  const stats = [
    { labelKey: 'admin_stat_total_users', labelFb: 'Total Users', value: analytics?.total_users || 0, icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { labelKey: 'admin_stat_premium_users', labelFb: 'Premium Users', value: analytics?.premium_users || 0, icon: Crown, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { labelKey: 'admin_stat_revenue', labelFb: 'Total Revenue', value: `₹${(analytics?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-green-500', bg: 'bg-green-500/10' },
    { labelKey: 'admin_stat_courses', labelFb: 'Courses', value: analytics?.total_courses || 0, icon: BookOpen, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { labelKey: 'admin_stat_lessons', labelFb: 'Lessons', value: analytics?.total_lessons || 0, icon: Video, color: 'text-pink-500', bg: 'bg-pink-500/10' },
    { labelKey: 'admin_stat_completion', labelFb: 'Completion Rate', value: `${analytics?.completion_rate || 0}%`, icon: TrendingUp, color: 'text-accent', bg: 'bg-accent/10' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">
            {t('admin_nav_dashboard', 'Dashboard')}
          </h1>
          <p className="text-muted-foreground">
            {t('admin_dash_subtitle', 'Platform overview and analytics')}
          </p>
        </div>
        <Button onClick={seedData} variant="outline" data-testid="seed-data-btn">
          <Database className="w-4 h-4 mr-2" />
          {t('admin_seed_sample', 'Seed Sample Data')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {stats.map((stat) => (
          <Card key={stat.labelKey}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">
                    {t(stat.labelKey, stat.labelFb)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin_quick_actions', 'Quick Actions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/courses')} className="h-auto p-4 flex-col">
              <Plus className="w-6 h-6 mb-2" />
              {t('admin_action_create_course', 'Create Course')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/users')} className="h-auto p-4 flex-col">
              <Users className="w-6 h-6 mb-2" />
              {t('admin_action_manage_users', 'Manage Users')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/settings')} className="h-auto p-4 flex-col">
              <Settings className="w-6 h-6 mb-2" />
              {t('admin_action_settings', 'Settings')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Users
export const AdminUsers = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [premiumFilter, setPremiumFilter] = useState('');
  const [importing, setImporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userTab, setUserTab] = useState('all');
  const fileInputRef = React.useRef(null);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'user', is_premium: false
  });
  const [unlockDialogUser, setUnlockDialogUser] = useState(null);
  const [unlockTargetId, setUnlockTargetId] = useState('');
  const [unlockScope, setUnlockScope] = useState('lesson');
  const [grantingUnlock, setGrantingUnlock] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const limit = 20;

  const headers = { Authorization: `Bearer ${token}` };

  // Used for the admin "Reset Password" flow — a random password the admin
  // can copy and hand to a user who never received (or lost) their own,
  // meeting the same 8-char + digit rule the backend enforces.
  const generatePassword = () => {
    const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';
    const digits = '23456789';
    const all = letters + digits;
    // Guarantee at least one digit (backend requires it) by fixing one slot,
    // then filling the rest randomly and shuffling.
    const chars = [digits[Math.floor(Math.random() * digits.length)]];
    for (let i = 0; i < 9; i++) chars.push(all[Math.floor(Math.random() * all.length)]);
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (userTab === 'admin') params.append('role', 'admin');
      else if (userTab === 'users') params.append('role', 'user');
      else if (roleFilter) params.append('role', roleFilter);
      if (premiumFilter) params.append('is_premium', premiumFilter);

      const res = await axios.get(`${API_URL}/admin/users?${params}`, { headers });
      setUsers(Array.isArray(res.data?.users) ? res.data.users : []);
      setTotal(res.data?.total || 0);
      setPages(res.data?.pages || 1);
    } catch (err) {
      console.error(err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token, search, currentPage, roleFilter, premiumFilter, userTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API_URL}/admin/users/${editingUser?.id}`, updateData, { headers });
        toast.success(t('admin_toast_user_updated', 'User updated'));
      } else {
        await axios.post(`${API_URL}/admin/users`, formData, { headers });
        toast.success(t('admin_toast_user_created', 'User created'));
      }
      setDialogOpen(false);
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: 'user', is_premium: false });
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_user_save_fail', 'Failed to save user'));
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ name: user?.name || '', email: user?.email || '', password: '', role: user?.role || 'user', is_premium: user?.is_premium || false });
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!userId || !window.confirm(t('admin_confirm_delete_user', 'Delete this user?'))) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, { headers });
      toast.success(t('admin_toast_user_deleted', 'User deleted'));
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_user_delete_fail', 'Failed to delete user'));
    }
  };

  const togglePremium = async (userId) => {
    if (!userId) return;
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/premium`, {}, { headers });
      toast.success(t('admin_toast_premium_updated', 'User premium status updated'));
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_user_update_fail', 'Failed to update user'));
    }
  };

  const handleGrantUnlock = async () => {
    if (!unlockDialogUser || !unlockTargetId.trim()) return;
    setGrantingUnlock(true);
    try {
      const payload = unlockScope === 'course'
        ? { course_id: unlockTargetId.trim() }
        : { lesson_id: unlockTargetId.trim() };
      await axios.post(`${API_URL}/admin/users/${unlockDialogUser.id}/lesson-unlocks`, payload, { headers });
      toast.success(t('admin_toast_unlock_granted', 'Re-access granted'));
      setUnlockDialogUser(null);
      setUnlockTargetId('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_unlock_fail', 'Failed to grant re-access'));
    } finally {
      setGrantingUnlock(false);
    }
  };

  const toggleDirectoryHidden = async (user) => {
    if (!user?.id) return;
    try {
      await axios.put(`${API_URL}/admin/users/${user.id}`, { directory_hidden: !user.directory_hidden }, { headers });
      toast.success(
        user.directory_hidden
          ? t('admin_toast_directory_shown', 'User restored to student directory')
          : t('admin_toast_directory_hidden', 'User hidden from student directory')
      );
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_user_update_fail', 'Failed to update user'));
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API_URL}/admin/users/export?format=${format}`, {
        headers,
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv' && res?.data) {
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'users_export.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      } else if (res?.data) {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'users_export.json';
        a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success(tFormat('admin_exported_users', { n: total }, 'Exported {n} users'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_export_failed', 'Export failed'));
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/admin/users/import`, formDataUpload, { headers });
      toast.success(tFormat('admin_imported_users', { n: res?.data?.imported || 0 }, 'Imported {n} users'));
      if (res?.data?.errors?.length > 0) {
        toast.warning(tFormat('admin_import_errors', { n: res.data.errors.length }, '{n} errors occurred'));
      }
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_import_failed', 'Import failed'));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">{t('admin_page_users', 'Users')}</h1>
          <p className="text-muted-foreground">
            {tFormat('admin_total_users_line', { n: total }, '{n} total users')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                {t('admin_export', 'Export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                {t('admin_export_csv', 'Export CSV')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')}>
                {t('admin_export_json', 'Export JSON')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImport}
            className="hidden"
          />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing}>
            <Upload className="w-4 h-4 mr-2" />
            {importing ? t('admin_importing', 'Importing...') : t('admin_import', 'Import')}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingUser(null); setFormData({ name: '', email: '', password: '', role: 'user', is_premium: false }); setShowPassword(false); }}>
                <UserPlus className="w-4 h-4 mr-2" />
                {t('admin_create_user', 'Create User')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingUser ? t('admin_edit_user', 'Edit User') : t('admin_create_user_title', 'Create User')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t('admin_lbl_name_req', 'Name *')}</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div>
                  <Label>{t('admin_lbl_email_req', 'Email *')}</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
                </div>
                {editingUser && (
                  <div>
                    <Label>{t('admin_lbl_phone', 'Phone')}</Label>
                    <Input value={editingUser.phone ? `${editingUser.phone_country_code || '+91'} ${editingUser.phone}` : t('admin_phone_not_set', 'Not set')} disabled readOnly />
                  </div>
                )}
                <div>
                  <Label>
                    {editingUser
                      ? t('admin_lbl_password_optional', 'Reset Password (leave blank to keep current)')
                      : t('admin_lbl_password_req', 'Password *')}
                  </Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required={!editingUser}
                        className="pr-9"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      title={t('admin_generate_password', 'Generate a password')}
                      onClick={() => { setFormData((f) => ({ ...f, password: generatePassword() })); setShowPassword(true); }}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    {formData.password && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        title={t('admin_copy_password', 'Copy password')}
                        onClick={() => {
                          navigator.clipboard.writeText(formData.password);
                          toast.success(t('admin_password_copied', 'Password copied'));
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {editingUser && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('admin_password_reset_hint', "Use this if the user can't sign in and didn't receive their password by email — generate or type one, then share it with them directly. Their other logged-in devices will be signed out.")}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('admin_lbl_role', 'Role')}</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">{t('admin_role_user', 'User')}</SelectItem>
                        <SelectItem value="admin">{t('admin_role_admin', 'Admin')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch checked={formData.is_premium} onCheckedChange={(v) => setFormData({...formData, is_premium: v})} />
                    <Label>{t('admin_lbl_premium_switch', 'Premium')}</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit">{editingUser ? t('admin_btn_update', 'Update') : t('admin_btn_create', 'Create')}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <Button variant={userTab === 'all' ? 'default' : 'outline'} onClick={() => { setUserTab('all'); setCurrentPage(1); }}>
          {t('admin_users_tab_all', 'All Users')}
        </Button>
        <Button variant={userTab === 'admin' ? 'default' : 'outline'} onClick={() => { setUserTab('admin'); setCurrentPage(1); }}>
          <Shield className="w-4 h-4 mr-2" />
          {t('admin_users_tab_admins', 'Admins')}
        </Button>
        <Button variant={userTab === 'users' ? 'default' : 'outline'} onClick={() => { setUserTab('users'); setCurrentPage(1); }}>
          <Users className="w-4 h-4 mr-2" />
          {t('admin_users_tab_regular', 'Regular Users')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('admin_search_users_ph', 'Search by name, email, or phone...')}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="pl-10"
                data-testid="user-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v === 'all' ? '' : v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder={t('admin_all_roles', 'All Roles')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin_all_roles', 'All Roles')}</SelectItem>
                <SelectItem value="user">{t('admin_role_user', 'User')}</SelectItem>
                <SelectItem value="admin">{t('admin_role_admin', 'Admin')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={premiumFilter} onValueChange={(v) => { setPremiumFilter(v === 'all' ? '' : v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder={t('admin_all_status', 'All Status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin_all_status', 'All Status')}</SelectItem>
                <SelectItem value="true">{t('admin_status_premium', 'Premium')}</SelectItem>
                <SelectItem value="false">{t('admin_status_free', 'Free')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin_col_name', 'Name')}</TableHead>
                <TableHead>{t('admin_col_email', 'Email')}</TableHead>
                <TableHead>{t('admin_col_role', 'Role')}</TableHead>
                <TableHead>{t('admin_col_premium', 'Premium')}</TableHead>
                <TableHead>{t('admin_col_points', 'Points')}</TableHead>
                <TableHead>{t('admin_col_joined', 'Joined')}</TableHead>
                <TableHead>{t('admin_col_actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    {t('admin_loading_users', 'Loading...')}
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {t('admin_users_empty', 'No users found')}
                  </TableCell>
                </TableRow>
              ) : users.map((user) => (
                <TableRow key={user.id} className={user.is_active === false ? 'opacity-50 bg-muted/40' : ''}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>
                    <div>{user.email}</div>
                    {user.phone && (
                      <div className="text-xs text-muted-foreground">
                        {user.phone_country_code || '+91'} {user.phone}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      <Badge variant={user.role !== 'user' ? 'default' : 'secondary'}>
                        {user.role}
                      </Badge>
                      {user.is_active === false && (
                        <Badge variant="destructive">{t('admin_status_deleted', 'Deleted')}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.is_premium ? (
                      <Badge className="bg-amber-500">{t('admin_status_premium', 'Premium')}</Badge>
                    ) : (
                      <Badge variant="outline">{t('admin_status_free', 'Free')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>{user.points || 0}</TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(user)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePremium(user.id)}
                        className="text-xs"
                      >
                        {user.is_premium
                          ? t('admin_users_remove_premium', 'Remove Premium')
                          : t('admin_users_grant_premium', 'Grant Premium')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleDirectoryHidden(user)}
                        title={user.directory_hidden
                          ? t('admin_users_show_directory', 'Show in Student Directory')
                          : t('admin_users_hide_directory', 'Hide from Student Directory')}
                      >
                        {user.directory_hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setUnlockDialogUser(user); setUnlockTargetId(''); setUnlockScope('lesson'); }}
                        title={t('admin_users_grant_reaccess', 'Grant Re-access (view limit)')}
                      >
                        <Unlock className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Dialog open={!!unlockDialogUser} onOpenChange={(open) => { if (!open) { setUnlockDialogUser(null); setUnlockTargetId(''); } }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{t('admin_unlock_dialog_title', 'Grant Re-access')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {tFormat('admin_unlock_dialog_desc', { name: unlockDialogUser?.name || '' }, 'Give {name} a fresh set of views for a lesson or course.')}
                </p>
                <div>
                  <Label>{t('admin_unlock_scope', 'Scope')}</Label>
                  <Select value={unlockScope} onValueChange={setUnlockScope}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lesson">{t('admin_unlock_scope_lesson', 'Lesson')}</SelectItem>
                      <SelectItem value="course">{t('admin_unlock_scope_course', 'Course')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{unlockScope === 'course' ? t('admin_unlock_course_id', 'Course ID') : t('admin_unlock_lesson_id', 'Lesson ID')}</Label>
                  <Input value={unlockTargetId} onChange={(e) => setUnlockTargetId(e.target.value)} placeholder={unlockScope === 'course' ? t('admin_unlock_course_id_ph', 'Enter course ID') : t('admin_unlock_lesson_id_ph', 'Enter lesson ID')} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleGrantUnlock} disabled={grantingUnlock || !unlockTargetId.trim()}>
                  {grantingUnlock ? t('admin_saving', 'Saving...') : t('admin_unlock_grant_btn', 'Grant Re-access')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                {tFormat(
                  'admin_users_pagination',
                  {
                    from: ((currentPage - 1) * limit) + 1,
                    to: Math.min(currentPage * limit, total),
                    total,
                  },
                  'Showing {from} to {to} of {total} users'
                )}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  {t('admin_btn_previous', 'Previous')}
                </Button>
                <span className="text-sm px-3">
                  {tFormat(
                    'admin_page_of_fmt',
                    { current: currentPage, pages },
                    'Page {current} of {pages}'
                  )}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(pages, p + 1))}
                  disabled={currentPage === pages}
                >
                  {t('admin_btn_next', 'Next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// Admin Courses
export const AdminCourses = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [importing, setImporting] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const fileInputRef = React.useRef(null);
  const thumbInputRef = React.useRef(null);
  const [formData, setFormData] = useState({
    title: '', description: '', category: '',
    thumbnail_url: '', is_free: false, order: 0, original_language: 'en', level: 'beginner',
    enable_video: true, enable_reviews: true, enable_comments: true, enable_content: true,
    unlimited_views: false
  });
  const [premiumWarning, setPremiumWarning] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  // High enough that every course fits on one page — drag-to-reorder needs the full list.
  const limit = 100;

  const handleFreeToggle = (newIsFree) => {
    setFormData(f => ({ ...f, is_free: newIsFree }));
    if (!editingCourse) {
      setPremiumWarning('');
      return;
    }
    setPremiumWarning(
      newIsFree
        ? 'Disabling premium will also mark ALL lessons in this course as free. All students will be able to access them.'
        : 'Enabling premium will also mark ALL lessons in this course as premium. Students without a subscription will lose access.'
    );
  };

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      
      const [coursesRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/courses?${params}`, { headers }),
        axios.get(`${API_URL}/categories`)
      ]);
      // Handle both array and paginated response
      const coursesData = coursesRes.data;
      if (Array.isArray(coursesData)) {
        setCourses(coursesData);
        setTotal(coursesData.length);
        setPages(1);
      } else {
        setCourses(coursesData.courses || []);
        setTotal(coursesData.total || 0);
        setPages(coursesData.pages || 1);
      }
      // Categories from API
      const cats = categoriesRes.data || [];
      setCategories(safeArray(cats));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, search, currentPage, categoryFilter]);

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingThumb(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const res = await axios.post(`${API_URL}/admin/upload/image`, uploadData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, thumbnail_url: res?.data?.url || '' });
      toast.success(t('admin_thumb_uploaded', 'Thumbnail uploaded'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_thumb_upload_fail', 'Failed to upload thumbnail'));
    } finally {
      setUploadingThumb(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API_URL}/admin/courses/export?format=${format}`, { 
        headers,
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv' && res?.data) {
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'courses_export.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      } else if (res?.data) {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'courses_export.json';
        a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success(tFormat('admin_exported_courses', { n: total }, 'Exported {n} courses'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_export_failed', 'Export failed'));
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await axios.post(`${API_URL}/admin/courses/import`, formDataUpload, { headers });
      toast.success(tFormat('admin_imported_courses', { n: res?.data?.imported || 0 }, 'Imported {n} courses'));
      if (res?.data?.errors?.length > 0) {
        toast.warning(tFormat('admin_import_errors', { n: res.data.errors.length }, '{n} errors occurred'));
      }
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_import_failed', 'Import failed'));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCourse) {
        const res = await axios.put(`${API_URL}/admin/courses/${editingCourse?.id}`, formData, { headers });
        toast.success(t('admin_course_updated', 'Course updated'));
        if (res?.data?.lessons_synced > 0) {
          toast.success(
            `${res.data.lessons_synced} lesson${res.data.lessons_synced === 1 ? '' : 's'} automatically set to ${res.data.lessons_is_free ? 'free 🔓' : 'premium 🔒'}`
          );
        }
      } else {
        await axios.post(`${API_URL}/admin/courses`, formData, { headers });
        toast.success(t('admin_course_created', 'Course created'));
      }
      setDialogOpen(false);
      setEditingCourse(null);
      setPremiumWarning('');
      setFormData({ title: '', description: '', category: '', thumbnail_url: '', is_free: false, order: 0, original_language: 'en', level: 'beginner', enable_video: true, enable_reviews: true, enable_comments: true, enable_content: true });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_course_save_fail', 'Failed to save course'));
    }
  };

  const handleEdit = (course) => {
    if (!course) return;
    setEditingCourse(course);
    setPremiumWarning('');
    const catVal = categoryId(course?.category) ?? course?.category ?? '';
    setFormData({
      ...course,
      category: catVal,
      description: course?.description ?? '',
      level: course?.level || 'beginner',
      enable_video: course?.enable_video !== false,
      enable_reviews: course?.enable_reviews !== false,
      enable_comments: course?.enable_comments !== false,
      enable_content: course?.enable_content !== false,
    });
    setDialogOpen(true);
  };

  // Reordering sends the full course list, so it's only possible on the unfiltered, single-page view.
  const canReorder = !search && !categoryFilter && pages === 1 && courses.length > 1;

  const handleCourseReorder = async (reordered) => {
    const previous = courses;
    setCourses(reordered.map((c, i) => ({ ...c, order: i + 1 })));
    setSavingOrder(true);
    try {
      await axios.put(`${API_URL}/admin/courses/reorder`, { ordered_ids: reordered.map(c => c.id) }, { headers });
      toast.success(t('admin_course_order_saved', 'Course order saved'));
    } catch (err) {
      setCourses(previous);
      toast.error(err?.response?.data?.detail || t('admin_course_order_fail', 'Failed to save course order'));
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!courseId || !window.confirm(t('admin_course_delete_confirm', 'Are you sure? This will delete all lessons too.'))) return;
    try {
      await axios.delete(`${API_URL}/admin/courses/${courseId}`, { headers });
      toast.success(t('admin_course_deleted', 'Course deleted'));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_course_delete_fail', 'Failed to delete course'));
    }
  };

  return (
    <div data-testid="admin-courses">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">{t('admin_page_courses', 'Courses')}</h1>
          <p className="text-muted-foreground">{tFormat('admin_total_courses_line', { n: total }, `${total} total courses`)}</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                {t('admin_export', 'Export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('csv')}>{t('admin_export_csv', 'Export CSV')}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')}>{t('admin_export_json', 'Export JSON')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImport}
            className="hidden"
          />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing}>
            <Upload className="w-4 h-4 mr-2" />
            {importing ? t('admin_importing', 'Importing...') : t('admin_import', 'Import')}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingCourse(null); setPremiumWarning(''); setFormData({ title: '', description: '', category: '', thumbnail_url: '', is_free: false, order: 0, original_language: 'en', level: 'beginner', enable_video: true, enable_reviews: true, enable_comments: true, enable_content: true }); }} data-testid="create-course-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('admin_create_course', 'Create Course')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingCourse ? t('admin_edit_course', 'Edit Course') : t('admin_new_course', 'Create Course')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t('admin_lbl_title_req', 'Title *')}</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="course-title-input" />
                </div>
                <div>
                  <Label>{t('admin_lbl_description_req', 'Description *')}</Label>
                  <RichTextEditor 
                    value={formData.description} 
                    onChange={(value) => setFormData({...formData, description: value})}
                    placeholder={t('admin_course_desc_placeholder', 'Write course description with rich formatting...')}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('admin_lbl_category_req', 'Category *')}</Label>
                    <Select value={formData.category || 'select'} onValueChange={(v) => setFormData({...formData, category: v === 'select' ? '' : v})}>
                      <SelectTrigger><SelectValue placeholder={t('admin_select_category', 'Select category')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select" disabled>{t('admin_select_category_item', 'Select a category')}</SelectItem>
                        {categories.map((cat) => {
                          const cid = cat.id ?? cat._id;
                          const cname = categoryLabel(cat) || cat.name || cid;
                          return (
                            <SelectItem key={cid} value={cid}>{cname}</SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('admin_lbl_level', 'Level')}</Label>
                    <Select value={formData.level || 'beginner'} onValueChange={(v) => setFormData({...formData, level: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">{t('level_beginner', 'Beginner')}</SelectItem>
                        <SelectItem value="intermediate">{t('level_intermediate', 'Intermediate')}</SelectItem>
                        <SelectItem value="advanced">{t('level_advanced', 'Advanced')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('admin_lbl_language', 'Language')}</Label>
                    <Select value={formData.original_language || 'en'} onValueChange={(v) => setFormData({...formData, original_language: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">{t('admin_lang_english', 'English')}</SelectItem>
                        <SelectItem value="ta">{t('admin_lang_tamil', 'Tamil')}</SelectItem>
                        <SelectItem value="hi">{t('admin_lang_hindi', 'Hindi')}</SelectItem>
                        <SelectItem value="es">{t('admin_lang_spanish', 'Spanish')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('admin_lbl_priority_order', 'Priority Order')}</Label>
                    <Input type="number" min={1} placeholder={t('admin_order_last_ph', 'Last')} value={formData.order || ''} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value, 10) || 0})} />
                    <p className="text-xs text-muted-foreground mt-1">{t('admin_course_order_help', 'Position in the course list — other courses shift automatically. You can also drag rows in the list.')}</p>
                  </div>
                </div>
                <div>
                  <Label>{t('admin_lbl_thumbnail', 'Thumbnail')}</Label>
                  <div className="flex items-center gap-4">
                    <input
                      ref={thumbInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      className="hidden"
                    />
                    <Button type="button" variant="outline" onClick={() => thumbInputRef.current?.click()} disabled={uploadingThumb}>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingThumb ? t('admin_uploading', 'Uploading...') : t('admin_upload_image', 'Upload Image')}
                    </Button>
                    <span className="text-sm text-muted-foreground">{t('admin_or', 'or')}</span>
                    <Input 
                      value={formData.thumbnail_url} 
                      onChange={(e) => setFormData({...formData, thumbnail_url: e.target.value})} 
                      placeholder={t('admin_paste_url', 'Paste URL...')}
                      className="flex-1"
                    />
                  </div>
                  {formData.thumbnail_url && (
                    <div className="mt-3 relative w-full max-w-xs aspect-video rounded-lg overflow-hidden border">
                      <img src={formData.thumbnail_url} alt={t('admin_thumb_preview_alt', 'Thumbnail preview')} className="w-full h-full object-cover" />
                      <Button 
                        type="button"
                        variant="destructive" 
                        size="icon" 
                        className="absolute top-2 right-2 w-6 h-6"
                        onClick={() => setFormData({...formData, thumbnail_url: ''})}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={formData.is_free} onCheckedChange={handleFreeToggle} data-testid="course-free-switch" />
                  <Label>{t('admin_lbl_free_course', 'Free Course')}</Label>
                </div>
                {premiumWarning && (
                  <div
                    className="flex items-start gap-2 rounded-lg border px-3.5 py-2.5 text-[13px]"
                    style={{ background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }}
                    data-testid="premium-sync-warning"
                  >
                    <span>⚠️</span>
                    <span>{premiumWarning}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Switch checked={!!formData.unlimited_views} onCheckedChange={(v) => setFormData({...formData, unlimited_views: v})} data-testid="course-unlimited-views-switch" />
                  <Label>{t('admin_lbl_unlimited_views', 'Unlimited Views (exempt from view limits)')}</Label>
                </div>

                {/* Section visibility toggles — control what learners see on
                    the course / lesson page. Defaults to ON for new courses. */}
                <div className="rounded-lg border bg-muted/30 p-4 space-y-3" data-testid="course-section-toggles">
                  <div>
                    <Label className="text-sm font-semibold">Section visibility</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Toggle which sections learners can see on this course.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="course-enable-video" className="text-sm">Video player</Label>
                      <Switch id="course-enable-video" checked={!!formData.enable_video} onCheckedChange={(v) => setFormData({...formData, enable_video: v})} data-testid="enable-video-switch" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="course-enable-content" className="text-sm">Course contents (lessons)</Label>
                      <Switch id="course-enable-content" checked={!!formData.enable_content} onCheckedChange={(v) => setFormData({...formData, enable_content: v})} data-testid="enable-content-switch" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="course-enable-reviews" className="text-sm">Reviews & ratings</Label>
                      <Switch id="course-enable-reviews" checked={!!formData.enable_reviews} onCheckedChange={(v) => setFormData({...formData, enable_reviews: v})} data-testid="enable-reviews-switch" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="course-enable-comments" className="text-sm">Comments</Label>
                      <Switch id="course-enable-comments" checked={!!formData.enable_comments} onCheckedChange={(v) => setFormData({...formData, enable_comments: v})} data-testid="enable-comments-switch" />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="save-course-btn">{editingCourse ? t('admin_btn_update', 'Update') : t('admin_btn_create', 'Create')}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('admin_search_courses_ph', 'Search courses...')}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="pl-10"
                data-testid="course-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v === 'all' ? '' : v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder={t('admin_all_categories', 'All Categories')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin_all_categories', 'All Categories')}</SelectItem>
                {categories.map((cat) => {
                  const cid = cat.id ?? cat._id;
                  const cname = categoryLabel(cat) || cat.name || cid;
                  return (
                    <SelectItem key={cid} value={cid}>{cname}</SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          
          <p className="text-sm text-muted-foreground mb-3" data-testid="course-reorder-hint">
            {canReorder
              ? (savingOrder
                  ? t('admin_course_order_saving', 'Saving order...')
                  : t('admin_course_reorder_hint', 'Drag the ⋮⋮ handle to reorder courses. Numbers update automatically and students see the same order.'))
              : t('admin_course_reorder_disabled', 'Clear the search and category filter to reorder courses.')}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">#</TableHead>
                <TableHead>{t('admin_col_title', 'Title')}</TableHead>
                <TableHead>{t('admin_col_category', 'Category')}</TableHead>
                <TableHead>{t('admin_col_language', 'Language')}</TableHead>
                <TableHead>{t('admin_col_status', 'Status')}</TableHead>
                <TableHead>{t('admin_col_actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">{t('admin_loading_users', 'Loading...')}</TableCell>
                </TableRow>
              ) : courses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">{t('admin_courses_empty', 'No courses found')}</TableCell>
                </TableRow>
              ) : (
                <SortableList
                  items={courses}
                  onReorder={handleCourseReorder}
                  disabled={!canReorder || savingOrder}
                  renderItem={(course, index, { ref, style, handleProps }) => (
                <TableRow key={course.id} ref={ref} style={style} className="bg-background" data-testid={`course-row-${course.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <DragHandle handleProps={handleProps} disabled={!canReorder || savingOrder} />
                      <span className="font-semibold tabular-nums" style={{ color: '#A67C52' }}>
                        {canReorder ? index + 1 : course.order}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{course.title}</TableCell>
                  <TableCell className="capitalize">{categoryLabel(course.category) || (typeof course.category === 'object' ? course.category?.name : course.category)}</TableCell>
                  <TableCell>{course.original_language?.toUpperCase() || 'EN'}</TableCell>
                  <TableCell>
                    {course.is_free ? (
                      <span
                        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold"
                        style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac' }}
                      >
                        🔓 {t('admin_status_free', 'Free')}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold"
                        style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}
                      >
                        🔒 {t('admin_status_premium', 'Premium')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(course)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(course.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                  )}
                />
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                {tFormat(
                  'admin_courses_pagination',
                  {
                    from: ((currentPage - 1) * limit) + 1,
                    to: Math.min(currentPage * limit, total),
                    total,
                  },
                  'Showing {from} to {to} of {total} courses'
                )}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm px-3">
                  {tFormat(
                    'admin_page_of_fmt',
                    { current: currentPage, pages },
                    'Page {current} of {pages}'
                  )}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(pages, p + 1))}
                  disabled={currentPage === pages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};



// Admin Settings
const ReaccessPackagesCard = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const headers = { Authorization: `Bearer ${token}` };

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', price: 0, currency: 'INR', scope: 'per_lesson', description: '' });
  const [saving, setSaving] = useState(false);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/reaccess-packages`, { headers });
      setPackages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(t('admin_toast_reaccess_pkgs_fail', 'Failed to load re-access packages'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPackages(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', price: 0, currency: 'INR', scope: 'per_lesson', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({
      name: pkg.name || '',
      price: pkg.price ?? 0,
      currency: pkg.currency || 'INR',
      scope: pkg.scope || 'per_lesson',
      description: pkg.description || '',
    });
    setDialogOpen(true);
  };

  const handleSavePackage = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/admin/reaccess-packages/${editingId}`, form, { headers });
      } else {
        await axios.post(`${API_URL}/admin/reaccess-packages`, form, { headers });
      }
      toast.success(t('admin_toast_reaccess_pkg_saved', 'Re-access package saved'));
      setDialogOpen(false);
      fetchPackages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_reaccess_pkg_fail', 'Failed to save package'));
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (pkg) => {
    try {
      await axios.put(`${API_URL}/admin/reaccess-packages/${pkg.id}`, { is_active: true }, { headers });
      toast.success(t('admin_toast_reaccess_pkg_activated', 'Package activated'));
      fetchPackages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_reaccess_pkg_fail', 'Failed to save package'));
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(t('admin_confirm_delete_reaccess_pkg', 'Delete this re-access package?'))) return;
    try {
      await axios.delete(`${API_URL}/admin/reaccess-packages/${pkg.id}`, { headers });
      toast.success(t('admin_toast_reaccess_pkg_deleted', 'Package deleted'));
      fetchPackages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_reaccess_pkg_fail', 'Failed to delete package'));
    }
  };

  return (
    <Card data-testid="admin-reaccess-packages-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>{t('admin_card_reaccess_packages', 'Re-access Packages')}</CardTitle>
        <Button size="sm" onClick={openCreate} data-testid="add-reaccess-package-btn">
          <Plus className="w-4 h-4 mr-1" />
          {t('admin_btn_add_package', 'Add Package')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">{t('admin_loading', 'Loading...')}</p>
        ) : packages.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('admin_no_reaccess_packages', 'No packages configured yet.')}</p>
        ) : (
          packages.map((pkg) => (
            <div key={pkg.id} className="flex items-center justify-between border rounded-lg p-3" data-testid="reaccess-package-row">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{pkg.name}</p>
                  {pkg.is_active && <Badge data-testid="package-active-badge">{t('admin_badge_active', 'Active')}</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">
                  ₹{pkg.price} · {pkg.scope === 'per_course' ? t('admin_reaccess_scope_course', 'Per Course') : t('admin_reaccess_scope_lesson', 'Per Lesson')}
                </p>
                {pkg.description && <p className="text-xs text-muted-foreground">{pkg.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                {!pkg.is_active && (
                  <Button size="sm" variant="outline" onClick={() => handleActivate(pkg)} data-testid="activate-package-btn">
                    {t('admin_btn_activate', 'Activate')}
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => openEdit(pkg)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => handleDelete(pkg)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
        <p className="text-xs text-muted-foreground">
          {t('admin_reaccess_pkg_help', "Only one package can be active at a time. Students see the active package's name and price on the locked-lesson screen.")}
        </p>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? t('admin_edit_package', 'Edit Package') : t('admin_add_package', 'Add Package')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('admin_lbl_package_name', 'Name')}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>{t('admin_lbl_package_price', 'Price (₹)')}</Label>
              <Input
                type="number"
                min="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>{t('admin_lbl_package_scope', 'Scope')}</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_lesson">{t('admin_reaccess_scope_lesson', 'Per Lesson')}</SelectItem>
                  <SelectItem value="per_course">{t('admin_reaccess_scope_course', 'Per Course')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('admin_lbl_package_description', 'Description')}</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSavePackage} disabled={saving}>
              {saving ? t('admin_saving', 'Saving...') : t('admin_save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const AdminSettings = () => {
  const { token, isSuperAdmin } = useAuth();
  const { t, tFormat } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fonts, setFonts] = useState([]);

  // Maintenance Mode — separate from the bulk settings save flow above:
  // toggling saves immediately via its own endpoint, super_admin only.
  const [maintenance, setMaintenance] = useState(null); // { maintenance_mode, affected_users }
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);

  // Promotional Popup — config lives inside `settings.popup_config`, saved via
  // the same bulk handleSave() as the rest of this page.
  const [popupStats, setPopupStats] = useState(null);
  const [popupPreviewOpen, setPopupPreviewOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!isSuperAdmin) return;
    axios.get(`${API_URL}/admin/settings/maintenance`, { headers })
      .then((res) => setMaintenance(res.data))
      .catch(() => setMaintenance(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, token]);

  useEffect(() => {
    axios.get(`${API_URL}/admin/popup/stats`, { headers })
      .then((res) => setPopupStats(res.data))
      .catch(() => setPopupStats(null)); // requires view_analytics — silently hide if not held
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const applyMaintenanceMode = async (nextValue) => {
    setMaintenanceSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/settings/maintenance`, { maintenance_mode: nextValue }, { headers });
      setMaintenance((m) => ({ ...m, maintenance_mode: res.data.maintenance_mode }));
      toast.success(
        nextValue
          ? t('admin_toast_maintenance_on', 'Maintenance mode enabled — users cannot log in')
          : t('admin_toast_maintenance_off', 'Platform restored')
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_maintenance_fail', 'Failed to update maintenance mode'));
    } finally {
      setMaintenanceSaving(false);
    }
  };

  const handleMaintenanceToggle = (checked) => {
    if (checked) {
      setConfirmEnableOpen(true);
    } else {
      applyMaintenanceMode(false);
    }
  };

  const confirmEnableMaintenance = () => {
    setConfirmEnableOpen(false);
    applyMaintenanceMode(true);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        setSettings(res?.data || {});
      } catch (err) {
        console.error(err);
        setSettings({});
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/fonts`)
      .then((res) => setFonts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setFonts([]));
  }, []);

  // Load every curated font so each dropdown option and the live preview
  // can render in its own font (admin-only preview, doesn't affect the
  // two-font limit applied to the public-facing site).
  useEffect(() => {
    if (!fonts.length) return;
    let link = document.getElementById('admin-typography-preview-fonts');
    if (!link) {
      link = document.createElement('link');
      link.id = 'admin-typography-preview-fonts';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = googleFontsHref(fonts.map((f) => f.value));
  }, [fonts]);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings`, settings, { headers });
      toast.success(t('admin_toast_settings_saved', 'Settings saved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_settings_fail', 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="animate-pulse">{t('admin_loading_settings', 'Loading settings...')}</div>;
  }

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">{t('admin_page_settings', 'Settings')}</h1>
          <p className="text-muted-foreground">
            {t('admin_settings_subtitle', 'Platform configuration')}
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? t('admin_saving', 'Saving...') : t('admin_save_settings', 'Save Settings')}
        </Button>
      </div>

      {isSuperAdmin && maintenance && (
        <Card className="mb-6 border-2" data-testid="admin-platform-access">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                {t('admin_card_platform_access', 'Platform Access')}
              </CardTitle>
              <Badge
                className={maintenance.maintenance_mode
                  ? 'bg-red-600 hover:bg-red-600 text-white'
                  : 'bg-green-600 hover:bg-green-600 text-white'}
              >
                {maintenance.maintenance_mode
                  ? t('admin_maintenance_status_active', 'Maintenance Mode Active')
                  : t('admin_maintenance_status_open', 'Platform Open')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>{t('admin_lbl_maintenance_mode', 'Maintenance Mode')}</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('admin_maintenance_desc', 'When enabled, only admin users can log in. Regular users will see a maintenance message.')}
                </p>
              </div>
              <Switch
                checked={maintenance.maintenance_mode}
                onCheckedChange={handleMaintenanceToggle}
                disabled={maintenanceSaving}
                data-testid="maintenance-mode-toggle"
              />
            </div>

            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{t('admin_maintenance_warning', 'Warning: This will immediately lock out all regular users.')}</span>
            </div>

            <p className="text-sm text-muted-foreground">
              {tFormat('admin_maintenance_affected', { count: maintenance.affected_users }, 'Currently: {count} users affected')}
            </p>

            <p className="text-xs text-muted-foreground">
              {t('admin_maintenance_sessions_note', 'Current sessions unaffected — already logged-in users can keep using the platform until their session expires.')}
            </p>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmEnableOpen} onOpenChange={setConfirmEnableOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin_maintenance_confirm_title', 'Enable Maintenance Mode?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {tFormat(
                'admin_maintenance_confirm_body',
                { count: maintenance?.affected_users ?? 0 },
                'All {count} regular users will be unable to log in until you disable this. Admins can still log in normally.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin_cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmEnableMaintenance} className="bg-red-600 text-white hover:bg-red-700">
              {t('admin_maintenance_confirm_action', 'Enable Maintenance Mode')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Branding */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin_card_branding', 'Branding')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t('admin_lbl_app_name', 'App Name')}</Label>
              <Input value={settings.app_name} onChange={(e) => setSettings({ ...settings, app_name: e.target.value })} />
            </div>
            <div>
              <Label>{t('admin_lbl_logo_url', 'Logo URL')}</Label>
              <Input value={settings.logo_url} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} placeholder={t('admin_ph_https', 'https://...')} />
            </div>
            <div>
              <Label>{t('admin_lbl_footer_text', 'Footer Text')}</Label>
              <Input value={settings.footer_text} onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })} />
            </div>
          </CardContent>
        </Card>

        {/* Share & QR */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin_card_share_qr', 'Share & QR')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-6">
            <QRCodeDisplay size={140} caption="Scan to visit website" showDownload={true} />
            <a
              href="/qr.html"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 text-xs text-accent hover:underline"
            >
              {t('admin_open_printable_qr', 'Open printable QR page')}
            </a>
          </CardContent>
        </Card>

        {/* Theme */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin_card_theme', 'Theme')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t('admin_lbl_primary_color', 'Primary Color')}</Label>
              <div className="flex gap-2">
                <Input value={settings.primary_color} onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })} />
                <div className="w-10 h-10 rounded border" style={{ backgroundColor: settings.primary_color }} />
              </div>
            </div>
            <div>
              <Label>{t('admin_lbl_accent_color', 'Accent Color')}</Label>
              <div className="flex gap-2">
                <Input value={settings.accent_color} onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })} />
                <div className="w-10 h-10 rounded border" style={{ backgroundColor: settings.accent_color }} />
              </div>
            </div>
            <div>
              <Label>{t('admin_lbl_theme_mode', 'Theme Mode')}</Label>
              <Select value={settings.theme_mode} onValueChange={(v) => setSettings({ ...settings, theme_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">{t('admin_theme_light', 'Light')}</SelectItem>
                  <SelectItem value="dark">{t('admin_theme_dark', 'Dark')}</SelectItem>
                  <SelectItem value="system">{t('admin_theme_system', 'System')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Pricing (single Premium Access plan — Monthly/Quarterly/Yearly retired,
            their price fields stay in the DB for existing subscribers but are no
            longer editable here) */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin_card_lifetime_admissions', 'Pricing')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>{t('admin_lbl_lifetime_enabled', 'Lifetime Plan Enabled')}</Label>
              <Switch checked={settings.lifetime_enabled !== false} onCheckedChange={(v) => setSettings({ ...settings, lifetime_enabled: v })} />
            </div>
            <div>
              <Label>{t('admin_lbl_lifetime_price', 'Lifetime Price (₹)')}</Label>
              <Input type="number" value={settings.lifetime_price ?? 4999} onChange={(e) => setSettings({ ...settings, lifetime_price: parseFloat(e.target.value) })} />
            </div>
            <div>
              <Label>{t('admin_lbl_lifetime_original_price', 'Lifetime Strikethrough Price (₹)')}</Label>
              <Input type="number" value={settings.lifetime_original_price ?? 9999} onChange={(e) => setSettings({ ...settings, lifetime_original_price: parseFloat(e.target.value) })} />
            </div>
            <div>
              <Label>{t('admin_lbl_lifetime_badge_text', 'Badge Text')}</Label>
              <Input value={settings.lifetime_badge_text ?? 'BEST VALUE'} onChange={(e) => setSettings({ ...settings, lifetime_badge_text: e.target.value })} placeholder="BEST VALUE" />
            </div>
            <div>
              <Label>{t('admin_lbl_gst_percentage', 'GST Rate (%)')}</Label>
              <Input type="number" value={settings.gst_percentage ?? 18} onChange={(e) => setSettings({ ...settings, gst_percentage: parseFloat(e.target.value) })} />
            </div>
            <div>
              <Label>{t('admin_lbl_affiliate_commission', 'Affiliate Commission (%)')}</Label>
              <Input type="number" value={settings.affiliate_percentage} onChange={(e) => setSettings({ ...settings, affiliate_percentage: parseFloat(e.target.value) })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>{t('admin_lbl_enable_coupons_switch', 'Enable Coupons')}</Label>
              <Switch checked={settings.enable_coupons !== false} onCheckedChange={(v) => setSettings({ ...settings, enable_coupons: v })} />
            </div>
            <div>
              <Label>{t('admin_lbl_admission_limit', 'Admission Limit (seats)')}</Label>
              <Input type="number" value={settings.admission_limit ?? 1000} onChange={(e) => setSettings({ ...settings, admission_limit: parseInt(e.target.value, 10) })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>{t('admin_lbl_show_admission_counter', 'Show Admission Counter')}</Label>
              <Switch checked={settings.show_admission_counter !== false} onCheckedChange={(v) => setSettings({ ...settings, show_admission_counter: v })} />
            </div>
          </CardContent>
        </Card>

        {/* Access Control */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin_card_access_control', 'Access Control')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t('admin_lbl_device_limit', 'Device Limit per User')}</Label>
              <Input type="number" value={settings.device_limit} onChange={(e) => setSettings({ ...settings, device_limit: parseInt(e.target.value) })} />
            </div>
            <div>
              <Label>{t('admin_lbl_free_lessons_limit', 'Free Lessons Limit')}</Label>
              <Input type="number" value={settings.free_lessons_limit} onChange={(e) => setSettings({ ...settings, free_lessons_limit: parseInt(e.target.value) })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>{t('admin_lbl_enable_downloads', 'Enable Downloads')}</Label>
              <Switch checked={settings.enable_download} onCheckedChange={(v) => setSettings({ ...settings, enable_download: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>{t('admin_lbl_enable_student_directory', 'Show Student Directory')}</Label>
              <Switch
                checked={settings.enable_student_directory !== false}
                onCheckedChange={(v) => setSettings({ ...settings, enable_student_directory: v })}
                data-testid="enable-student-directory-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Lesson View Limits */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin_card_view_limits', 'Lesson View Limits')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start justify-between gap-4 pb-4 border-b">
              <div>
                <Label>{t('admin_lbl_lock_on_completion', 'Lock Lessons After Completion')}</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {t(
                    'admin_hint_lock_on_completion',
                    'On: a completed lesson\'s content is covered by a "purchase re-access" lock. Off (default): students can freely re-watch completed lessons. Server-enforced limits are the View Limits below.'
                  )}
                </p>
              </div>
              <Switch
                checked={settings.lock_lessons_on_completion === true}
                onCheckedChange={(v) => setSettings({ ...settings, lock_lessons_on_completion: v })}
                data-testid="lock-on-completion-toggle"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>{t('admin_lbl_view_limit_enabled', 'Enable View Limits')}</Label>
              <Switch
                checked={settings.view_limit_enabled === true}
                onCheckedChange={(v) => setSettings({ ...settings, view_limit_enabled: v })}
                data-testid="view-limit-enabled-toggle"
              />
            </div>
            <div>
              <Label>{t('admin_lbl_max_views_per_lesson', 'Max Views per Lesson')}</Label>
              <Input
                type="number"
                min="1"
                value={settings.max_views_per_lesson ?? 1}
                onChange={(e) => setSettings({ ...settings, max_views_per_lesson: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div>
              <Label>{t('admin_lbl_reattempt_price', 'Re-unlock Price (₹)')}</Label>
              <Input
                type="number"
                min="0"
                value={settings.reattempt_price ?? 0}
                onChange={(e) => setSettings({ ...settings, reattempt_price: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>{t('admin_lbl_view_limit_scope', 'Re-unlock Scope')}</Label>
              <Select value={settings.view_limit_scope || 'lesson'} onValueChange={(v) => setSettings({ ...settings, view_limit_scope: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lesson">{t('admin_view_limit_scope_lesson', 'Per Lesson')}</SelectItem>
                  <SelectItem value="course">{t('admin_view_limit_scope_course', 'Per Course')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('admin_lbl_view_completion_threshold', 'Completion Threshold (%)')}</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={settings.view_completion_threshold ?? 90}
                onChange={(e) => setSettings({ ...settings, view_completion_threshold: parseInt(e.target.value) || 90 })}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('admin_view_limit_help', 'When enabled, users are blocked from re-watching a lesson after reaching the max views, unless they purchase a re-unlock or the lesson/course is marked "unlimited views".')}
            </p>
          </CardContent>
        </Card>

        {/* Re-access Packages */}
        <ReaccessPackagesCard />

        {/* Payments */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin_card_payment_gateways', 'Payment Gateways')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>{t('admin_lbl_enable_stripe', 'Enable Stripe')}</Label>
              <Switch checked={settings.enable_stripe} onCheckedChange={(v) => setSettings({ ...settings, enable_stripe: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>{t('admin_lbl_enable_paypal', 'Enable PayPal')}</Label>
              <Switch checked={settings.enable_paypal} onCheckedChange={(v) => setSettings({ ...settings, enable_paypal: v })} />
            </div>
          </CardContent>
        </Card>

        {/* Language */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin_card_language', 'Language')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t('admin_lbl_default_language', 'Default Language')}</Label>
              <Select value={settings.default_language} onValueChange={(v) => setSettings({ ...settings, default_language: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">{t('admin_lang_english', 'English')}</SelectItem>
                  <SelectItem value="ta">{t('admin_lang_tamil', 'Tamil')}</SelectItem>
                  <SelectItem value="hi">{t('admin_lang_hindi', 'Hindi')}</SelectItem>
                  <SelectItem value="es">{t('admin_lang_spanish', 'Spanish')}</SelectItem>
                  <SelectItem value="fr">{t('admin_lang_french', 'French')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Typography */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('admin_card_typography', 'Typography')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('admin_lbl_heading_font', 'Heading Font')}</Label>
                <Select
                  value={settings.heading_font || DEFAULT_HEADING_FONT}
                  onValueChange={(v) => setSettings({ ...settings, heading_font: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {fonts.map((font) => (
                      <SelectItem key={font.value} value={font.value} style={{ fontFamily: `'${font.value}', ${font.category}` }}>
                        {font.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('admin_lbl_body_font', 'Body Font')}</Label>
                <Select
                  value={settings.body_font || DEFAULT_BODY_FONT}
                  onValueChange={(v) => setSettings({ ...settings, body_font: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {fonts.map((font) => (
                      <SelectItem key={font.value} value={font.value} style={{ fontFamily: `'${font.value}', ${font.category}` }}>
                        {font.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-2 bg-muted/30">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('admin_lbl_typography_preview', 'Live Preview')}
              </p>
              <p
                className="text-2xl font-medium"
                style={{ fontFamily: `'${settings.heading_font || DEFAULT_HEADING_FONT}', serif` }}
              >
                {t('admin_typography_preview_heading', 'Master Interior Design & Carpentry')}
              </p>
              <p
                style={{ fontFamily: `'${settings.body_font || DEFAULT_BODY_FONT}', sans-serif` }}
              >
                {t('admin_typography_preview_body', 'The quick brown fox jumps over the lazy dog. 0123456789')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Promotional Popup (Share & Earn + Upgrade CTA) */}
        <Card className="lg:col-span-2" data-testid="admin-popup-settings">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5" />
                {t('admin_card_popup', 'Promotional Popup')}
              </CardTitle>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPopupPreviewOpen(true)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {t('admin_btn_preview_popup', 'Preview Popup')}
                </Button>
                <Switch
                  checked={settings.popup_config?.enabled !== false}
                  onCheckedChange={(v) => setSettings({
                    ...settings,
                    popup_config: { ...settings.popup_config, enabled: v }
                  })}
                  data-testid="popup-enabled-toggle"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                {t('admin_popup_section_triggers', 'Trigger Settings')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <Label>{t('admin_popup_show_on_login', 'Show after login')}</Label>
                  <Switch
                    checked={settings.popup_config?.show_on_login !== false}
                    onCheckedChange={(v) => setSettings({
                      ...settings,
                      popup_config: { ...settings.popup_config, show_on_login: v }
                    })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>{t('admin_popup_show_on_lesson', 'Show after lesson complete')}</Label>
                  <Switch
                    checked={settings.popup_config?.show_on_lesson_complete !== false}
                    onCheckedChange={(v) => setSettings({
                      ...settings,
                      popup_config: { ...settings.popup_config, show_on_lesson_complete: v }
                    })}
                  />
                </div>
                <div>
                  <Label>{t('admin_popup_delay', 'Delay before showing (seconds)')}</Label>
                  <Input
                    type="number"
                    min={0}
                    value={settings.popup_config?.delay_seconds ?? 2}
                    onChange={(e) => setSettings({
                      ...settings,
                      popup_config: { ...settings.popup_config, delay_seconds: Number(e.target.value) }
                    })}
                  />
                </div>
                <div>
                  <Label>{t('admin_popup_hours_between', 'Hours between shows')}</Label>
                  <Input
                    type="number"
                    min={0}
                    value={settings.popup_config?.hours_between_shows ?? 24}
                    onChange={(e) => setSettings({
                      ...settings,
                      popup_config: { ...settings.popup_config, hours_between_shows: Number(e.target.value) }
                    })}
                  />
                </div>
                <div>
                  <Label>{t('admin_popup_max_dismissals', 'Max dismissals before permanent suppression')}</Label>
                  <Input
                    type="number"
                    min={1}
                    value={settings.popup_config?.max_dismissals ?? 3}
                    onChange={(e) => setSettings({
                      ...settings,
                      popup_config: { ...settings.popup_config, max_dismissals: Number(e.target.value) }
                    })}
                  />
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                {t('admin_popup_section_content', 'Content')}
              </p>
              <div className="space-y-4">
                <div>
                  <Label>{t('admin_popup_headline', 'Headline')}</Label>
                  <Input
                    value={settings.popup_config?.headline ?? 'Unlock Premium Access to All Courses'}
                    onChange={(e) => setSettings({
                      ...settings,
                      popup_config: { ...settings.popup_config, headline: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label>{t('admin_popup_cta_primary', 'Primary CTA text')}</Label>
                  <Input
                    value={settings.popup_config?.cta_primary ?? 'Get Premium Access Now'}
                    onChange={(e) => setSettings({
                      ...settings,
                      popup_config: { ...settings.popup_config, cta_primary: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label>{t('admin_popup_share_message', 'WhatsApp share message')}</Label>
                  <Textarea
                    rows={3}
                    value={settings.popup_config?.share_message ?? "I'm learning Interior Design on Thachari! Join me and get access to all courses. {link}"}
                    onChange={(e) => setSettings({
                      ...settings,
                      popup_config: { ...settings.popup_config, share_message: e.target.value }
                    })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('admin_popup_share_message_hint', 'Use {link} as placeholder for the referral link')}
                  </p>
                </div>
              </div>
            </div>

            {popupStats && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                  {t('admin_popup_section_analytics', 'Analytics')}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-2xl font-bold">{popupStats.total_shown}</p>
                    <p className="text-muted-foreground">{t('admin_popup_stat_shown', 'Total shown')}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{popupStats.total_dismissed}</p>
                    <p className="text-muted-foreground">{t('admin_popup_stat_dismissed', 'Dismissed')}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{popupStats.total_clicked_share}</p>
                    <p className="text-muted-foreground">{t('admin_popup_stat_shared', 'Clicked share')}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {popupStats.total_converted} <span className="text-sm font-medium">({popupStats.conversion_rate})</span>
                    </p>
                    <p className="text-muted-foreground">{t('admin_popup_stat_converted', 'Converted')}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {tFormat(
                    'admin_popup_suppressed_note',
                    { count: popupStats.suppressed_users },
                    'Currently suppressed for {count} users (max dismissals reached)'
                  )}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {popupPreviewOpen && (
        <PromoPopup
          previewMode
          popupConfig={settings.popup_config}
          admissionCount={{ remaining: 214 }}
          onClose={() => setPopupPreviewOpen(false)}
        />
      )}
    </div>
  );
};
